import {GetIdPropertyOptions, GetIdsPropertyOptions, IDs} from "../../general/options.ts";
import VectorSource from "ol/source/Vector";
import Feature from "ol/Feature";

/**
 * The name of the property that represents an object's ID.
 * @private
 * @constant {string}
 */
const ID_PROPERTY = 'id';
const ATTRIBUTE_PROPERTY_NAME = 'attribute';

/**
 * Name of the internal bookkeeping property that records which named style a
 * live feature currently renders with (see `Layer.setFeatureStyle`). It is
 * OpenLayers-side state, not data: it must never be copied onto a NEW feature
 * built from another feature's attributes (the copy would claim a style it does
 * not have — e.g. a `_select` style on a feature that is not selected), and it
 * does not belong in returned GeoJSON attributes either.
 * @constant {string}
 */
export const CURRENT_STYLENAME_PROPERTY = 'currentStyleName';

/**
 * Gets the name of the property that represents an object's ID.
 * @param {object} options - An optional options object that may contain an `attribute` property specifying the name of the ID property.
 * @param {string} attribute - An optional name of the attribute used to specify the ID property in the `options` object. Defaults to `ATTRIBUTE_PROPERTY_NAME`.
 * @returns {string} The name of the ID property.
 */
export function getIdProperty(
    options?: GetIdPropertyOptions | GetIdsPropertyOptions,
    attribute: 'attribute' | 'attribute1' | 'attribute2' = ATTRIBUTE_PROPERTY_NAME
): string {
    if (!options) {
        return ID_PROPERTY;
    }

    if (attribute === 'attribute' && 'attribute' in options) {
        return options.attribute ?? ID_PROPERTY;
    }

    if (attribute === 'attribute1' && 'attribute1' in options) {
        return options.attribute1 ?? ID_PROPERTY;
    }

    if (attribute === 'attribute2' && 'attribute2' in options) {
        return options.attribute2 ?? ID_PROPERTY;
    }

    return ID_PROPERTY;
}

/**
 * Find the highest integer value stored under an id property across every
 * feature in a source. Non-integer ids (strings, floats, missing values) are
 * ignored. Returns `0` when no feature carries an integer id, so the next id to
 * hand out is always `highestIntegerId(...) + 1`.
 *
 * Shared by drawing (a freshly drawn polygon is numbered one above the current
 * maximum) and by spatial operations that need to give a generated feature an
 * id the consumer can later query with `getFeatureByID`.
 *
 * @param source - The OpenLayers VectorSource to scan.
 * @param idProperty - Feature attribute that holds the id (e.g. "id").
 * @returns The highest integer id found, or `0` when none exists.
 */
export function highestIntegerId(source: VectorSource, idProperty: string): number {
    let highest = 0;

    source.forEachFeature((feature: Feature) => {
        const id = feature.get(idProperty);
        if (Number.isInteger(id) && id > highest) {
            highest = id;
        }
    });

    return highest;
}

const normalize = (v: unknown) => (v == null ? '' : String(v));

/**
 * Find features in a source by attribute value(s).
 *
 * @param source - The OpenLayers VectorSource
 * @param attribute   - Feature attribute to check (e.g., "id" or "uuid")
 * @param ids    - A single id or an array of ids
 */
export function getFeaturesFromSource<T extends Feature = Feature>(
    source: VectorSource<T>,
    attribute: string,
    ids: IDs
): T[] {
    const wanted = Array.isArray(ids)
        ? new Set(Array.from(ids, normalize))
        : new Set([normalize(ids)]);

    return source.getFeatures().filter((feature) => wanted.has(normalize(feature.get(attribute))));
}